import { createContext, useContext, type ReactNode } from "react";

import type {
  DerivedPageAssetSlot,
  PageAssetsPayload,
  ResolvedPageAssetView,
  SinglePageAssetSlot,
} from "~/lib/assets/slots";

/**
 * T219 — client accessor over the loader-resolved page-asset payload.
 *
 * The surface's loader calls `resolvePageAssets(...)` (page-assets.server.ts)
 * and the view mounts `<PageAssetsProvider assets={...}>`; `usePageAsset` reads
 * from context only — never fetch (loader →
 * component data flow). A missing provider or unresolved slot degrades to the
 * placeholder path in <SlotImage> / <SlotVideo>.
 */
const PageAssetsContext = createContext<PageAssetsPayload | null>(null);

export function PageAssetsProvider({
  assets,
  children,
}: {
  assets: PageAssetsPayload;
  children: ReactNode;
}) {
  return <PageAssetsContext.Provider value={assets}>{children}</PageAssetsContext.Provider>;
}

export function usePageAsset(slot: SinglePageAssetSlot | DerivedPageAssetSlot): ResolvedPageAssetView | null {
  const assets = useContext(PageAssetsContext);
  const found = assets?.[slot];
  // The payload's dynamic half (T428 per-question slots) is typed loosely enough to hold a
  // collection, which this accessor never returns — narrow rather than cast.
  return found == null || Array.isArray(found) ? null : found;
}

